import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Zap, Wand2, ArrowLeft, Image as ImageIcon, Globe, Lock } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import Header from "@/components/Header";

const QUICK_TEMPLATES = [
  "Witty barista who reads tarot cards on the weekends",
  "Adventurous travel photographer just back from Iceland",
  "Quiet astrophysics grad student who is obsessed with vinyl",
  "Brooklyn pastry chef with a soft spot for stray cats",
];

export default function AIDatingCreate() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("quick");
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Shared
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [avatarUrl, setAvatarUrl] = useState(null);

  // Quick mode
  const [shortDescription, setShortDescription] = useState("");

  // Advanced mode
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [personality, setPersonality] = useState("");
  const [interests, setInterests] = useState("");
  const [bio, setBio] = useState("");

  const [avatarPrompt, setAvatarPrompt] = useState("");

  const buildPayload = () => ({
    mode,
    name: name.trim(),
    visibility,
    avatar_url: avatarUrl,
    short_description: mode === "quick" ? shortDescription.trim() : null,
    age: mode === "advanced" && age ? Number(age) : null,
    gender: mode === "advanced" ? gender.trim() || null : null,
    personality: mode === "advanced" ? personality.trim() || null : null,
    interests:
      mode === "advanced"
        ? interests.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    bio: mode === "advanced" ? bio.trim() || null : null,
  });

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (mode === "quick" && !shortDescription.trim()) {
      toast.error("Add a quick description so they have a personality");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/ai-dating/characters`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 403 && err?.detail?.code === "kyc_required") {
          toast.error("KYC required to create AI dates");
          navigate("/kyc");
          return;
        }
        toast.error(err.detail || "Failed to create");
        return;
      }
      const created = await res.json();
      toast.success(`${created.name} is ready to chat`);
      navigate(`/ai-dating/chat/${created.character_id}`);
    } catch (e) {
      toast.error("Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGenerating(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/upload/image`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (res.ok) {
        const data = await res.json();
        setAvatarUrl(data.url);
        toast.success("Photo uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAvatar = async () => {
    // We need a character to attach the avatar to. Create-then-generate flow:
    if (!name.trim()) {
      toast.error("Add a name first");
      return;
    }
    if (mode === "quick" && !shortDescription.trim()) {
      toast.error("Add a description first");
      return;
    }
    setGenerating(true);
    try {
      // 1) Create the character without an avatar
      const createRes = await fetch(`${API}/ai-dating/characters`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(buildPayload()),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        if (createRes.status === 403 && err?.detail?.code === "kyc_required") {
          toast.error("KYC required");
          navigate("/kyc");
          return;
        }
        toast.error(err.detail || "Failed to create");
        return;
      }
      const created = await createRes.json();

      // 2) Generate avatar
      const genRes = await fetch(
        `${API}/ai-dating/characters/${created.character_id}/generate-avatar`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ prompt: avatarPrompt.trim() }),
        }
      );
      if (!genRes.ok) {
        const err = await genRes.json().catch(() => ({}));
        toast.error(err.detail || "Avatar generation failed");
        // Still navigate so the user can chat
        navigate(`/ai-dating/chat/${created.character_id}`);
        return;
      }
      toast.success("Avatar generated");
      navigate(`/ai-dating/chat/${created.character_id}`);
    } catch (e) {
      toast.error("Failed to generate");
    } finally {
      setGenerating(false);
    }
  };

  const busy = submitting || generating;

  return (
    <div className="min-h-screen bg-[#000033] text-[#FF66FF]">
      <Header />
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <button
          onClick={() => navigate("/ai-dating")}
          className="myspace-btn text-xs mb-4 flex items-center gap-1"
          data-testid="ai-create-back-btn"
        >
          <ArrowLeft className="w-3 h-3" />
          Back
        </button>

        <h1 className="text-3xl font-bold glitter-text mb-1" data-testid="ai-create-title">
          Design your AI date
        </h1>
        <p className="text-sm text-[#FF66FF] mb-5">
          Quick mode in one sentence, or go advanced for full control.
        </p>

        <div className="flex gap-1 mb-4 border-b-2 border-[#6600CC]">
          <button
            onClick={() => setMode("quick")}
            className={`nav-link flex items-center gap-1 text-sm ${mode === "quick" ? "active" : ""}`}
            data-testid="ai-create-mode-quick"
          >
            <Zap className="w-4 h-4" />
            Quick
          </button>
          <button
            onClick={() => setMode("advanced")}
            className={`nav-link flex items-center gap-1 text-sm ${mode === "advanced" ? "active" : ""}`}
            data-testid="ai-create-mode-advanced"
          >
            <Wand2 className="w-4 h-4" />
            Advanced
          </button>
        </div>

        <div className="brutal-card p-4 space-y-4">
          <div>
            <label className="block text-xs font-bold text-[#00FFFF] mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="Aria, Kai, Sam..."
              className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-[#FFFFFF]"
              data-testid="ai-create-name-input"
            />
          </div>

          {mode === "quick" ? (
            <>
              <div>
                <label className="block text-xs font-bold text-[#00FFFF] mb-1">
                  Describe them in one or two sentences *
                </label>
                <textarea
                  value={shortDescription}
                  onChange={(e) => setShortDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="A witty barista who reads tarot on the weekends..."
                  className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-[#FFFFFF]"
                  data-testid="ai-create-short-desc"
                />
                <div className="mt-1 flex flex-wrap gap-1">
                  {QUICK_TEMPLATES.map((t) => (
                    <button
                      key={t}
                      onClick={() => setShortDescription(t)}
                      className="text-[10px] px-1.5 py-0.5 border border-[#FF00FF] text-[#FF00FF] hover:bg-[rgba(255,0,255,0.15)]"
                      data-testid="ai-create-template-btn"
                    >
                      {t.slice(0, 40)}…
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#00FFFF] mb-1">Age</label>
                  <input
                    type="number"
                    min={18}
                    max={120}
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-[#FFFFFF]"
                    data-testid="ai-create-age-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#00FFFF] mb-1">Gender</label>
                  <input
                    type="text"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    placeholder="any"
                    className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-[#FFFFFF]"
                    data-testid="ai-create-gender-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#00FFFF] mb-1">Personality</label>
                <input
                  type="text"
                  value={personality}
                  onChange={(e) => setPersonality(e.target.value)}
                  placeholder="warm, witty, a little sarcastic"
                  className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-[#FFFFFF]"
                  data-testid="ai-create-personality-input"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#00FFFF] mb-1">
                  Interests (comma-separated)
                </label>
                <input
                  type="text"
                  value={interests}
                  onChange={(e) => setInterests(e.target.value)}
                  placeholder="indie music, hiking, vintage cameras"
                  className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-[#FFFFFF]"
                  data-testid="ai-create-interests-input"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#00FFFF] mb-1">Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={4}
                  maxLength={1200}
                  placeholder="Their backstory, vibe, the way they text..."
                  className="w-full bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-[#FFFFFF]"
                  data-testid="ai-create-bio-input"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-bold text-[#00FFFF] mb-1">Visibility</label>
            <div className="flex gap-2">
              <button
                onClick={() => setVisibility("private")}
                className={`flex-1 myspace-btn text-xs flex items-center justify-center gap-1 py-1 ${
                  visibility === "private" ? "active" : ""
                }`}
                data-testid="ai-create-visibility-private"
              >
                <Lock className="w-3 h-3" />
                Private (only me)
              </button>
              <button
                onClick={() => setVisibility("public")}
                className={`flex-1 myspace-btn text-xs flex items-center justify-center gap-1 py-1 ${
                  visibility === "public" ? "active" : ""
                }`}
                data-testid="ai-create-visibility-public"
              >
                <Globe className="w-3 h-3" />
                Public (anyone can date)
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-[#00FFFF] mb-1">
              Avatar (optional)
            </label>
            {avatarUrl && (
              <img
                src={avatarUrl.startsWith("http") ? avatarUrl : `${process.env.REACT_APP_BACKEND_URL}${avatarUrl}`}
                alt="avatar preview"
                className="w-20 h-20 border-2 border-[#FF00FF] object-cover mb-2"
                data-testid="ai-create-avatar-preview"
              />
            )}
            <div className="flex flex-col gap-2">
              <label className="myspace-btn text-xs flex items-center gap-1 cursor-pointer w-fit px-2 py-1">
                <ImageIcon className="w-3 h-3" />
                Upload photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadAvatar}
                  className="hidden"
                  data-testid="ai-create-avatar-upload"
                />
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={avatarPrompt}
                  onChange={(e) => setAvatarPrompt(e.target.value)}
                  placeholder="OR describe an AI-generated avatar (optional)"
                  className="flex-1 bg-[#000022] border-2 border-[#6600CC] px-2 py-1 text-[#FFFFFF] text-xs"
                  data-testid="ai-create-avatar-prompt"
                />
                <button
                  onClick={handleGenerateAvatar}
                  disabled={busy}
                  className="myspace-btn text-xs px-2 py-1 disabled:opacity-50 flex items-center gap-1"
                  data-testid="ai-create-generate-btn"
                >
                  <Sparkles className="w-3 h-3" />
                  {generating ? "..." : "Generate & Create"}
                </button>
              </div>
              <p className="text-[10px] text-[#888]">
                AI generation creates the character + the avatar in one click.
              </p>
            </div>
          </div>

          <button
            onClick={handleCreate}
            disabled={busy}
            className="w-full myspace-btn py-2 text-sm disabled:opacity-50"
            data-testid="ai-create-submit-btn"
          >
            {submitting ? "Creating..." : "Create & start chatting"}
          </button>
        </div>
      </div>
    </div>
  );
}
